import { useState, type ReactNode } from 'react'
import type { AiCredentialProvider, AiCredentialsSettings } from '@rp/contracts'
import { isApiError } from '../../api/client'
import { API_COPY } from '../../api/copy'
import { Button, Card, Dialog, Spinner } from '../../design'
import { AI_CREDENTIALS_COPY, AI_CREDENTIALS_SECTION_ID } from './aiCredentialsCopy'
import { AiCredentialsDetails } from './AiCredentialsDetails'
import { AnthropicCredentialsForm } from './AnthropicCredentialsForm'
import {
  useAiCredentials,
  useRemoveAiCredentials,
  useSaveAnthropicCredentials,
  useSaveTypeSafeCredentials,
  useSaveVertexCredentials,
} from './aiCredentialsQueries'
import { TypeSafeCredentialsForm } from './TypeSafeCredentialsForm'
import { VertexCredentialsForm } from './VertexCredentialsForm'

const HEADING_ID = 'ai-credentials-heading'

const REMOVE_COPY: Record<AiCredentialProvider, { title: string; body: string }> = {
  vertex_gemini: { title: AI_CREDENTIALS_COPY.removeVertexTitle, body: AI_CREDENTIALS_COPY.removeVertexBody },
  typesafe_jev: { title: AI_CREDENTIALS_COPY.removeTypeSafeTitle, body: AI_CREDENTIALS_COPY.removeTypeSafeBody },
  anthropic_claude: { title: AI_CREDENTIALS_COPY.removeAnthropicTitle, body: AI_CREDENTIALS_COPY.removeAnthropicBody },
}

const errorMessageOf = (error: unknown): string | null => (isApiError(error) ? error.message : null)

interface ProviderBlockProps {
  data: AiCredentialsSettings
  onRemove: (provider: AiCredentialProvider) => void
}

const VertexBlock = ({ data, onRemove }: ProviderBlockProps) => {
  const save = useSaveVertexCredentials()
  const [editing, setEditing] = useState(false)
  const vertex = data.vertex
  const stopEditing = () => {
    setEditing(false)
    save.reset()
  }

  return (
    <section className="ai-credentials-provider" aria-labelledby="ai-credentials-vertex">
      <h3 id="ai-credentials-vertex">{AI_CREDENTIALS_COPY.vertexTitle}</h3>
      <p className="settings-hint measure">{AI_CREDENTIALS_COPY.vertexHint}</p>
      {vertex !== null && !editing ? (
        <AiCredentialsDetails
          rows={[
            { label: AI_CREDENTIALS_COPY.projectId, value: vertex.projectId },
            { label: AI_CREDENTIALS_COPY.location, value: vertex.location },
            { label: AI_CREDENTIALS_COPY.clientEmail, value: vertex.clientEmail },
            { label: AI_CREDENTIALS_COPY.chatModel, value: vertex.chatModel },
            { label: AI_CREDENTIALS_COPY.embeddingModel, value: vertex.embeddingModel },
          ]}
          secretLabel={AI_CREDENTIALS_COPY.privateKey}
          secretUpdatedAt={vertex.secretUpdatedAt}
          verifiedAt={vertex.verifiedAt}
          onEdit={() => setEditing(true)}
          onRemove={() => onRemove('vertex_gemini')}
        />
      ) : (
        <VertexCredentialsForm
          current={vertex}
          pending={save.isPending}
          error={errorMessageOf(save.error)}
          onSubmit={(body) => save.mutate(body, { onSuccess: () => setEditing(false) })}
          {...(vertex === null ? {} : { onCancel: stopEditing })}
        />
      )}
    </section>
  )
}

const TypeSafeBlock = ({ data, onRemove }: ProviderBlockProps) => {
  const save = useSaveTypeSafeCredentials()
  const [editing, setEditing] = useState(false)
  const typesafe = data.typesafe
  const stopEditing = () => {
    setEditing(false)
    save.reset()
  }

  return (
    <section className="ai-credentials-provider" aria-labelledby="ai-credentials-typesafe">
      <h3 id="ai-credentials-typesafe">{AI_CREDENTIALS_COPY.typesafeTitle}</h3>
      <p className="settings-hint measure">
        {typesafe === null ? AI_CREDENTIALS_COPY.typesafeMissing : AI_CREDENTIALS_COPY.typesafeHint}
      </p>
      {typesafe !== null && !editing ? (
        <AiCredentialsDetails
          rows={[{ label: AI_CREDENTIALS_COPY.model, value: typesafe.model }]}
          secretLabel={AI_CREDENTIALS_COPY.apiKey}
          secretUpdatedAt={typesafe.secretUpdatedAt}
          verifiedAt={typesafe.verifiedAt}
          onEdit={() => setEditing(true)}
          onRemove={() => onRemove('typesafe_jev')}
        />
      ) : (
        <TypeSafeCredentialsForm
          current={typesafe}
          pending={save.isPending}
          error={errorMessageOf(save.error)}
          onSubmit={(body) => save.mutate(body, { onSuccess: () => setEditing(false) })}
          {...(typesafe === null ? {} : { onCancel: stopEditing })}
        />
      )}
    </section>
  )
}

const AnthropicBlock = ({ data, onRemove }: ProviderBlockProps) => {
  const save = useSaveAnthropicCredentials()
  const [editing, setEditing] = useState(false)
  const anthropic = data.anthropic
  const stopEditing = () => {
    setEditing(false)
    save.reset()
  }

  return (
    <section className="ai-credentials-provider" aria-labelledby="ai-credentials-anthropic">
      <h3 id="ai-credentials-anthropic">{AI_CREDENTIALS_COPY.anthropicTitle}</h3>
      <p className="settings-hint measure">
        {anthropic === null ? AI_CREDENTIALS_COPY.anthropicMissing : AI_CREDENTIALS_COPY.anthropicHint}
      </p>
      {anthropic === null || anthropic.usable ? null : (
        <p className="ai-credentials-paused text-tone tone-warning" role="status">
          {AI_CREDENTIALS_COPY.anthropicUnusable}
        </p>
      )}
      {anthropic !== null && !editing ? (
        <AiCredentialsDetails
          rows={[{ label: AI_CREDENTIALS_COPY.anthropicModel, value: anthropic.model }]}
          secretLabel={AI_CREDENTIALS_COPY.apiKey}
          secretUpdatedAt={anthropic.secretUpdatedAt}
          verifiedAt={anthropic.verifiedAt}
          onEdit={() => setEditing(true)}
          onRemove={() => onRemove('anthropic_claude')}
        />
      ) : (
        <AnthropicCredentialsForm
          current={anthropic}
          pending={save.isPending}
          error={errorMessageOf(save.error)}
          onSubmit={(body) => save.mutate(body, { onSuccess: () => setEditing(false) })}
          {...(anthropic === null ? {} : { onCancel: stopEditing })}
        />
      )}
    </section>
  )
}

interface RemoveDialogProps {
  provider: AiCredentialProvider | null
  pending: boolean
  error: string | null
  onConfirm: () => void
  onClose: () => void
}

const RemoveDialog = ({ provider, pending, error, onConfirm, onClose }: RemoveDialogProps) => (
  <Dialog
    open={provider !== null}
    title={provider === null ? '' : REMOVE_COPY[provider].title}
    onClose={onClose}
    footer={
      <>
        <Button onClick={onClose}>{AI_CREDENTIALS_COPY.cancel}</Button>
        <Button variant="danger-solid" pending={pending} pendingLabel={AI_CREDENTIALS_COPY.removing} onClick={onConfirm}>
          {AI_CREDENTIALS_COPY.confirmRemove}
        </Button>
      </>
    }
  >
    <p className="measure">{provider === null ? '' : REMOVE_COPY[provider].body}</p>
    {error === null ? null : (
      <p className="settings-error text-tone tone-danger" role="alert">
        {error}
      </p>
    )}
  </Dialog>
)

const SectionCard = ({ children }: { children: ReactNode }) => (
  <Card as="section" className="settings-card">
    <section className="ai-credentials-section" id={AI_CREDENTIALS_SECTION_ID} aria-labelledby={HEADING_ID}>
      {children}
    </section>
  </Card>
)

export const AiCredentialsSection = () => {
  const credentials = useAiCredentials()
  const remove = useRemoveAiCredentials()
  const [removing, setRemoving] = useState<AiCredentialProvider | null>(null)

  const closeDialog = () => {
    setRemoving(null)
    remove.reset()
  }
  const confirmRemove = () => {
    if (removing !== null) remove.mutate(removing, { onSuccess: () => setRemoving(null) })
  }

  if (credentials.isPending) {
    return (
      <SectionCard>
        <Spinner label={AI_CREDENTIALS_COPY.loading} />
      </SectionCard>
    )
  }
  if (credentials.isError) {
    return (
      <SectionCard>
        <p className="settings-error text-tone tone-danger" role="alert">
          {errorMessageOf(credentials.error) ?? API_COPY.malformed}
        </p>
      </SectionCard>
    )
  }

  const data = credentials.data
  return (
    <SectionCard>
      <h2 id={HEADING_ID}>{AI_CREDENTIALS_COPY.title}</h2>
      <p className="settings-hint measure">{AI_CREDENTIALS_COPY.intro}</p>
      {data.aiAvailable ? null : (
        <p className="ai-credentials-paused text-tone tone-warning" role="status">
          {AI_CREDENTIALS_COPY.unavailable}
        </p>
      )}
      <VertexBlock data={data} onRemove={setRemoving} />
      <TypeSafeBlock data={data} onRemove={setRemoving} />
      <AnthropicBlock data={data} onRemove={setRemoving} />
      <RemoveDialog
        provider={removing}
        pending={remove.isPending}
        error={errorMessageOf(remove.error)}
        onConfirm={confirmRemove}
        onClose={closeDialog}
      />
    </SectionCard>
  )
}
